import { DevicesList } from '@/features/devices/components/DevicesList/DevicesList'
import { Button, Text } from '@/ui-kit'
import { useMemo } from 'react'
import { RestrictedResourceModel } from '../api/models'
import {
  useConfirmWorkerAuthentication,
  useGrantAccessToRestrictedResource,
  useRestrictedWorkersList,
  useRevokeAccessToRestrictedResource
} from '../api/restricted-resources'
import styles from './RestrictedResource.module.css'
import { COLUMN_DEFS, STATUS_FORMATTER } from './devices-table-config'

export const RestrictedResource = ({ resource }: { resource: RestrictedResourceModel }) => {
  const { id, name, description, hasAccess, link, rewardsMultiplier } = resource
  const { data: workersList = [], isLoading: isFetchingResources } = useRestrictedWorkersList(
    resource.id,
    resource.hasAccess
  )
  const { mutateAsync: grantAccess, isPending: isPendingGrantAccess } = useGrantAccessToRestrictedResource()
  const { mutateAsync: revokeAccess, isPending: isPendingRevokeAccess } = useRevokeAccessToRestrictedResource()
  const { mutateAsync: confirmAuth, isPending, variables } = useConfirmWorkerAuthentication()

  const columnDefs = useMemo(
    () =>
      COLUMN_DEFS({
        resourceId: id,
        statusFormatter: STATUS_FORMATTER(link),
        isConfirmLoading: isPending,
        isLoadingWorkerId: variables?.workerId,
        onConfirmCallback: confirmAuth
      }),
    [link, id, isPending, confirmAuth, variables?.workerId]
  )

  const onToggleAccess = async (value: boolean) => {
    if (value) {
      await grantAccess(id)
    } else {
      await revokeAccess(id)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.info}>
        {/*         <div className={styles.logo}></div> */}
        <div className="flex flex-1 pl-4 max-mobile:flex-col max-mobile:gap-4 max-mobile:pl-0">
          <div className="flex flex-1 flex-col justify-center">
            <div className='flex gap-2'>
              <Text type="subtitle-semibold">{name}</Text>
              <div className="rounded-sm bg-[var(--colorGreen)] text-[12px] font-semibold self-start px-2">Multiplier {rewardsMultiplier}x</div>
            </div>
            <Text type="body1-regular" className={styles.description}>
              {description}
            </Text>
          </div>
          <div className={styles.buttons}>
            {!hasAccess ? (
              <Button
                className={styles.button}
                size="small"
                onClick={() => onToggleAccess(true)}
                isLoading={isPendingGrantAccess}
              >
                Give access
              </Button>
            ) : (
              <Button
                className={styles.button}
                size="small"
                variant="bordered-gray"
                onClick={() => onToggleAccess(false)}
                isLoading={isPendingRevokeAccess}
              >
                Revoke access
              </Button>
            )}
          </div>
        </div>
      </div>
      {hasAccess && (
        <DevicesList
          columns={columnDefs}
          workers={workersList}
          isFetching={isFetchingResources}
          meta={{ noHover: true, placeholderText: "You don't have any active devices." }}
        />
      )}
    </div>
  )
}
